// Admin-shell interactivity: a global "layout" store driving the responsive
// sidebar (drawer on mobile, static-but-collapsible on desktop).
import type { Alpine } from "alpinejs";

// The desktop collapse is remembered per-browser. _Head reads this same key synchronously and
// applies the class before first paint, so a collapsed shell never flashes open on load.
const COLLAPSED_KEY = "crm.sidebar.collapsed.v1";
const DESKTOP = "(min-width: 1024px)";

interface LayoutStore {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  openSidebar(): void;
  closeSidebar(): void;
  toggleSidebar(): void;
  collapseSidebar(value?: boolean): void;
}

function isDesktop(): boolean {
  return window.matchMedia(DESKTOP).matches;
}

export function registerShell(alpine: Alpine): void {
  const layout: LayoutStore = {
    // Mobile drawer state (desktop shows the sidebar unless it was collapsed).
    sidebarOpen: false,
    // Seeded from the class _Head already applied — the store must not re-read storage and
    // disagree with what is on screen.
    sidebarCollapsed:
      document.documentElement.classList.contains("sidebar-collapsed"),

    openSidebar() {
      this.sidebarOpen = true;
    },
    closeSidebar() {
      this.sidebarOpen = false;
    },
    // One control for both breakpoints: collapses/expands on desktop, opens the drawer on mobile.
    toggleSidebar() {
      if (isDesktop()) this.collapseSidebar(!this.sidebarCollapsed);
      else this.sidebarOpen = !this.sidebarOpen;
    },
    collapseSidebar(value?: boolean) {
      this.sidebarCollapsed = value ?? !this.sidebarCollapsed;
      document.documentElement.classList.toggle(
        "sidebar-collapsed",
        this.sidebarCollapsed,
      );
      try {
        localStorage.setItem(COLLAPSED_KEY, this.sidebarCollapsed ? "1" : "0");
      } catch {
        /* storage may be unavailable (private mode) — the choice just won't stick */
      }
    },
  };

  alpine.store("layout", layout);

  // Close the mobile drawer whenever the viewport grows to desktop width.
  window.matchMedia(DESKTOP).addEventListener("change", (e) => {
    if (e.matches) (alpine.store("layout") as LayoutStore).sidebarOpen = false;
  });
}
